import React, { useState, useEffect } from "react";
import { Modal, Input, message } from "antd";
import {
  ShopOutlined,
  AppstoreOutlined,
  DisconnectOutlined,
  ExportOutlined,
} from "@ant-design/icons";
import styled from "styled-components";
import {
  useGetConnectedStoreQuery,
  useInitiateShopifyOAuthMutation,
  useDisconnectShopifyMutation,
} from "@/api/shopifyApi";

const PlatformCard = styled.div`
  border: 1px solid #dfe3e8;
  border-radius: 8px;
  padding: 24px;
  width: 280px;
  background: #fff;
  display: flex;
  flex-direction: column;
  gap: 14px;
  @media (max-width: 768px) {
    width: 100%;
  }
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const CardTitle = styled.h3`
  font-family: Poppins;
  font-size: 16px;
  font-weight: 600;
  color: #000;
  margin: 0;
  flex: 1;
`;

const ConnectedBadge = styled.span`
  background: #f6ffed;
  border: 1px solid #b7eb8f;
  border-radius: 10px;
  padding: 2px 10px;
  font-size: 12px;
  font-family: Inter;
  color: #52c41a;
  font-weight: 500;
`;

const CardDesc = styled.p`
  font-family: Inter;
  font-size: 13px;
  color: #585858;
  margin: 0;
  line-height: 1.6;
`;

const StoreLabel = styled.div`
  font-family: Inter;
  font-size: 14px;
  color: #585858;
  background: #f5f5f5;
  border-radius: 4px;
  padding: 8px 12px;
  word-break: break-all;
`;

const ActionRow = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const ConnectBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 10px 20px;
  background-color: #000;
  color: #fff;
  border: none;
  border-radius: 8px;
  font-family: Inter;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  width: 100%;
  transition: all 0.2s ease;

  &:hover {
    background-color: #333;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }

  &:active {
    transform: translateY(0);
  }
`;

const ExternalBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  background: none;
  border: 1px solid #dfe3e8;
  border-radius: 6px;
  padding: 6px 12px;
  font-family: Inter;
  font-size: 13px;
  color: #585858;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: #aaa;
    color: #000;
  }
`;

const DisconnectBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  background: none;
  border: 1px solid #ffd8d8;
  border-radius: 6px;
  padding: 6px 12px;
  font-family: Inter;
  font-size: 13px;
  color: #ff4d4f;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #fff1f0;
    border-color: #ff4d4f;
  }
`;

export const ShopifyConnect = () => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [shopDomain, setShopDomain] = useState("");
  const { data: store, refetch } = useGetConnectedStoreQuery(
    undefined,
    { skip: false }
  );
  const [initiateOAuth, { isLoading: isInitiating }] =
    useInitiateShopifyOAuthMutation();
  const [disconnectShop, { isLoading: isDisconnecting }] =
    useDisconnectShopifyMutation();

  useEffect(() => {
    refetch();
  }, [refetch]);

  const handleConnect = async () => {
    if (!shopDomain.trim()) {
      message.error("Please enter a valid store domain");
      return;
    }

    try {
      const result = await initiateOAuth(shopDomain.trim()).unwrap();
      if (result.authUrl) {
        window.location.href = result.authUrl;
      }
    } catch (error) {
      message.error(
        error?.data?.message || "Failed to initiate Shopify connection"
      );
    }
  };

  const handleDisconnect = async () => {
    Modal.confirm({
      title: "Disconnect Shopify Store?",
      content: "Are you sure you want to disconnect your Shopify store?",
      okText: "Yes",
      cancelText: "No",
      onOk: async () => {
        try {
          await disconnectShop().unwrap();
          message.success("Store disconnected successfully");
          refetch();
        } catch (error) {
          message.error(
            error?.data?.message || "Failed to disconnect store"
          );
        }
      },
    });
  };

  if (store && store.id) {
    return (
      <PlatformCard>
        <CardHeader>
          <AppstoreOutlined style={{ fontSize: 22, color: "#52c41a" }} />
          <CardTitle>Shopify</CardTitle>
          <ConnectedBadge>Connected</ConnectedBadge>
        </CardHeader>
        <StoreLabel>{store.storeName || store.shopDomain}</StoreLabel>
        <ActionRow>
          <ExternalBtn
            onClick={() =>
              window.open(`https://${store.shopDomain}/admin`, "_blank")
            }
          >
            <ExportOutlined /> Open Store
          </ExternalBtn>
          <DisconnectBtn onClick={handleDisconnect} disabled={isDisconnecting}>
            <DisconnectOutlined /> Disconnect
          </DisconnectBtn>
        </ActionRow>
      </PlatformCard>
    );
  }

  return (
    <>
      <PlatformCard>
        <CardHeader>
          <AppstoreOutlined style={{ fontSize: 22, color: "#ed6928" }} />
          <CardTitle>Shopify</CardTitle>
        </CardHeader>
        <CardDesc>
          Connect your Shopify store to sync products and automate order
          fulfillment.
        </CardDesc>
        <ConnectBtn onClick={() => setIsModalVisible(true)}>
          <ShopOutlined />
          Connect Shopify
        </ConnectBtn>
      </PlatformCard>

      <Modal
        title={
          <span>
            <ShopOutlined /> Connect to Shopify
          </span>
        }
        open={isModalVisible}
        onOk={handleConnect}
        onCancel={() => {
          setIsModalVisible(false);
          setShopDomain("");
        }}
        confirmLoading={isInitiating}
        okText="Connect"
        cancelText="Cancel"
      >
        <div style={{ marginBottom: "16px" }}>
          <p>Enter your Shopify store domain to connect:</p>
          <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
            <Input
              placeholder="your-store"
              value={shopDomain}
              onChange={(e) => setShopDomain(e.target.value)}
              onPressEnter={handleConnect}
              disabled={isInitiating}
            />
            <span style={{ whiteSpace: "nowrap" }}>.myshopify.com</span>
          </div>
          <p style={{ marginTop: "8px", fontSize: "12px", color: "#999" }}>
            Enter just the store name (e.g., "mystore"), not the full URL
          </p>
        </div>
      </Modal>
    </>
  );
};
